import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import type { Venue, CreateVenuePayload, UpdateVenuePayload, OrgLocation } from "../types/venue.types";

// Generates a URL-safe slug from a venue name
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Public — no auth needed (anon RLS policy allows SELECT on active locations)
// Org data (name, sports, clubs_enabled) lives in the location's own metadata — no join needed.
// Falls back to the system default location (is_default=true) when slug is unrecognised.
export const getPublicVenue = async (slug: string): Promise<Venue | null> => {
  const VENUE_FIELDS = 'id, org_id, venue_name, venue_slug, sport, address, geolocation, emergency_info, metadata, is_default';

  const { data, error } = await supabase
    .from('base_organisation_location')
    .select(VENUE_FIELDS)
    .eq('venue_slug', slug)
    .eq('is_active', true)
    .single();

  console.log('[venueService] getPublicVenue slug:', slug, 'data:', data, 'error:', error);

  if (!error && data) {
    return data as Venue;
  }

  console.warn('[venueService] No venue for slug:', slug, '— falling back to default org location');

  const { data: fallback, error: fbError } = await supabase
    .from('base_organisation_location')
    .select(VENUE_FIELDS)
    .eq('is_default', true)
    .eq('is_active', true)
    .limit(1)
    .single();

  if (fbError || !fallback) {
    console.error('[venueService] Default org location fallback also failed:', fbError);
    return null;
  }

  return fallback as Venue;
};

// Fetch the system default org location (is_default=true) — used when no QR/venue context
export const getDefaultOrgVenue = async (): Promise<Venue | null> => {
  const { data, error } = await supabase
    .from('base_organisation_location')
    .select('id, org_id, venue_name, venue_slug, sport, address, geolocation, emergency_info, metadata, is_default')
    .eq('is_default', true)
    .eq('is_active', true)
    .limit(1)
    .single();

  if (error || !data) {
    console.error('[venueService] getDefaultOrgVenue failed:', error);
    return null;
  }
  return data as Venue;
};

// Fetch all locations (clubs) for an organisation
// Excludes the is_default org-level location so only real clubs appear in the sharing list
export const getOrgLocations = async (orgId: string): Promise<OrgLocation[]> => {
  const { data, error } = await supabase
    .from('base_organisation_location')
    .select('id, venue_name, venue_slug, address, geolocation, is_default, metadata')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .eq('is_default', false)
    .order('venue_name', { ascending: true });

  if (error) {
    console.error('[venueService] getOrgLocations error:', error);
    return [];
  }

  return (data ?? []) as OrgLocation[];
};

// Find the nearest org location to a given coordinate using Haversine distance
export const findNearestOrgLocation = (
  locations: OrgLocation[],
  userLat: number,
  userLng: number,
  maxDistanceKm = 50
): OrgLocation | null => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  let nearest: OrgLocation | null = null;
  let nearestDist = Infinity;

  for (const loc of locations) {
    if (!loc.geolocation) continue;
    const dist = haversineKm(userLat, userLng, loc.geolocation.lat, loc.geolocation.lng);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = loc;
    }
  }

  return nearestDist <= maxDistanceKm ? nearest : null;
};

// Share assessment with a club/location
export const shareAssessmentWithClub = async (payload: {
  incident_id?: string | null;
  session_id: string;
  org_id: string;
  club_id: string;
  shared_by_user_id?: string | null;
}): Promise<boolean> => {
  const { error } = await supabase
    .schema('charlie')
    .from('assessment_shares')
    .insert({
      incident_id: payload.incident_id ?? null,
      session_id: payload.session_id,
      org_id: payload.org_id,
      club_id: payload.club_id,
      share_type: 'club',
      shared_by_user_id: payload.shared_by_user_id ?? null,
    });

  if (error) {
    console.error('[venueService] shareAssessmentWithClub error:', error);
    return false;
  }
  return true;
};

// Authenticated — RLS filters by org automatically
export const listVenues = async (): Promise<Venue[]> => {
  const { data, error } = await supabase
    .from('base_organisation_location')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to list venues:', error);
    throw new Error('Failed to load venues');
  }

  return (data ?? []) as Venue[];
};

export const createVenue = async (payload: CreateVenuePayload): Promise<Venue> => {
  const slug = payload.venue_slug || generateSlug(payload.venue_name);

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  // Get user's tenant_id (org) from their profile via RPC
  const { data: profileData } = await supabase
    .rpc('get_user_profile', { user_id: userId })
    .single();

  if (!profileData?.tenant_id) {
    throw new Error('No organization found for current user');
  }

  const { data, error } = await supabase
    .from('base_organisation_location')
    .insert({
      ...payload,
      venue_slug: slug,
      org_id: profileData.tenant_id,
      created_by: userId,
      address: payload.address ?? {},
      emergency_info: payload.emergency_info ?? {},
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create venue:', error);
    throw new Error(error.message);
  }

  return data as Venue;
};

export const updateVenue = async (id: string, payload: UpdateVenuePayload): Promise<Venue> => {
  const { data, error } = await supabase
    .from('base_organisation_location')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Failed to update venue:', error);
    throw new Error(error.message);
  }

  return data as Venue;
};

// Soft delete — sets is_active = false
export const deleteVenue = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('base_organisation_location')
    .update({ is_active: false })
    .eq('id', id);

  if (error) {
    console.error('Failed to deactivate venue:', error);
    throw new Error(error.message);
  }
};
